import { get } from './http';

/**
 * Service to handle analytics and metadata for Purchase Order Automation.
 */
export const poAutomationAnalyticsService = {
  /**
   * Get performance analytics for a given date range.
   * @param {string} startDate - The start date in 'YYYY-MM-DD' format.
   * @param {string} endDate - The end date in 'YYYY-MM-DD' format.
   * @returns {Promise<any>} A promise that resolves with the analytics data.
   */
  async getAnalytics(startDate, endDate) {
    // The technical design specifies the endpoint as /api/v1/po-automation/analytics
    // The `get` function from http.ts will handle the full URL construction.
    return await get('/po-automation/analytics', {
      startDate,
      endDate,
    });
  },

  /**
   * Get the history of PO generation runs with pagination.
   * @param {object} options - Pagination options.
   * @param {number} options.page - The page number to fetch.
   * @param {number} options.limit - The number of items per page.
   * @param {string} [options.sortBy] - The field to sort by.
   * @param {string} [options.sortOrder] - The sort order ('asc' or 'desc').
   * @returns {Promise<any>} A promise that resolves with the paginated history data.
   */
  async getGenerationHistory(options = {}) {
    const { page = 1, limit = 10, sortBy = 'runDate', sortOrder = 'desc' } = options;
    
    // The technical design specifies the endpoint as /api/v1/po-automation/metadata
    return await get('/po-automation/metadata', {
      page,
      limit,
      sortBy,
      sortOrder
    });
  },

  /**
   * Get the detailed information for a specific execution run.
   * @param {string | number} executionId - The ID of the execution run.
   * @returns {Promise<any>} A promise that resolves with the detailed execution data.
   */
  async getExecutionDetails(executionId) {
    // The technical design specifies the endpoint as /api/v1/po-automation/metadata/:id
    return await get(`/po-automation/metadata/${executionId}`);
  }
};
